/**
 * Tiny external store for the DM dock's open-state + total-unread badge (GR-2).
 *
 * The chat ENTRY POINT lives in the header (Navbar's chat-indicator icon) while
 * the docked chat BOX renders bottom-right (MiniDmDock). Those two components sit
 * in different parts of the tree, so they coordinate through this module-level
 * store (via useSyncExternalStore) instead of a shared React context — fully
 * additive, no provider wiring, byte-identical when CHAT_ENABLED is OFF (nothing
 * imports it in that path).
 *
 * State:
 *   open     — is the bottom-right dock panel showing
 *   unread   — the header badge label (e.g. "3" / "9+") or null, published by the
 *              dock (which already fetches conversations) so the header icon can
 *              show it without a second data layer
 *   openerEl — the element that opened the dock (the header icon), so focus can
 *              return to it on close (a11y — mirrors the old launcher's focus story)
 *   activeId — id of the conversation currently open in the dock, PERSISTED here
 *              (bug #204c). MiniDmDock's `active` is component-local useState, so it
 *              is lost when the dock remounts (expand → full /messages* screen →
 *              browser Back rehydrates the dock). Keeping the id in the store lets the
 *              remounted dock seed itself back to the same thread instead of the list.
 *              Cleared on every GENUINE close (collapse / Escape / header toggle-off)
 *              so a real close never reopens onto a stale thread.
 *   activeName — display name captured at open time, for the seeded header before the
 *              conversations query rehydrates the full row.
 *   activeConversation — the COMPLETE conversation row (peer name/avatar/hue source)
 *              captured at the handoff, so a remounted/adopting dock can render the real
 *              peer even when the conversations query has not (yet) loaded that row.
 *              QA-1021/QA-1029: without it the minimize handoff seeded only { id }, so
 *              `conversationDisplayName({id})` → "Unknown" (amber-fallback tint) whenever
 *              `conversations.find()` missed. `liveActive` still PREFERS the live query
 *              row when present, so this snapshot is a completeness fallback, never a
 *              staleness source.
 *   expandOrigin — the app path the dock was floating over when the user hit Expand
 *              ("Open full screen"). The full /messages* screen suppresses the corner
 *              dock (it would duplicate the inbox), so the expanded Thread's new
 *              Minimize control (QA-1007) reads this to navigate BACK to the page the
 *              dock lives on — restoring the floating box instead of leaving the user
 *              on the full screen. Null (deep-linked straight to /messages/:id) falls
 *              back to the gallery.
 */
import { useSyncExternalStore } from "react";

let state = {
  open: false,
  unread: null,
  openerEl: null,
  activeId: null,
  activeName: null,
  activeConversation: null,
  expandOrigin: null,
};
const listeners = new Set();

const emit = () => {
  for (const l of listeners) l();
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Current store snapshot. Exported for tests + useSyncExternalStore. */
export const getSnapshot = () => state;

export const openDock = (openerEl = null) => {
  if (state.open && state.openerEl === openerEl) return;
  state = { ...state, open: true, openerEl };
  emit();
};

export const closeDock = () => {
  if (!state.open) return;
  // Genuine close clears the active conversation so a later reopen lands on the
  // conversation list, never a stale thread (bug #204c clear-on-close).
  state = {
    ...state,
    open: false,
    activeId: null,
    activeName: null,
    activeConversation: null,
  };
  emit();
};

export const toggleDock = (openerEl = null) => {
  state = state.open
    ? {
        ...state,
        open: false,
        activeId: null,
        activeName: null,
        activeConversation: null,
      }
    : { ...state, open: true, openerEl };
  emit();
};

/**
 * Persist the dock's active conversation so it survives a remount (bug #204c).
 * Called by MiniDmDock whenever a conversation is opened.
 */
export const setActiveConversation = ({
  id,
  name = null,
  conversation = null,
} = {}) => {
  if (id == null) {
    clearActiveConversation();
    return;
  }
  if (
    state.activeId === id &&
    state.activeName === name &&
    state.activeConversation === conversation
  )
    return;
  // QA-1021/QA-1029: persist the COMPLETE conversation object (peer name/avatar/
  // hue source) so an adopting/remounting dock renders the real peer + brand
  // palette even before its own conversations query loads that row.
  state = { ...state, activeId: id, activeName: name, activeConversation: conversation };
  emit();
};

/** Drop the persisted active conversation (Back button / left conversation). */
export const clearActiveConversation = () => {
  if (
    state.activeId == null &&
    state.activeName == null &&
    state.activeConversation == null
  )
    return;
  state = { ...state, activeId: null, activeName: null, activeConversation: null };
  emit();
};

/**
 * Remember the page the dock was floating over at Expand time, so the expanded
 * Thread's Minimize control (QA-1007) can navigate back to it. Set by MiniDmDock's
 * expandToFull; read by Thread's handleMinimize.
 */
export const setExpandOrigin = (path = null) => {
  if (state.expandOrigin === path) return;
  state = { ...state, expandOrigin: path };
  emit();
};

export const setUnread = (unread) => {
  if (state.unread === unread) return;
  state = { ...state, unread };
  emit();
};

/** Full store (open + unread + openerEl) — used by the dock. */
export const useDockStore = () => useSyncExternalStore(subscribe, getSnapshot);

/** Header icon only needs the unread label; subscribe narrowly. */
export const useDockUnread = () =>
  useSyncExternalStore(subscribe, () => state.unread);
